import { DefaultButton, Panel, PanelType, PrimaryButton, Stack } from "@fluentui/react";
import { AxiosError } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { localize } from "src/l10n";
import api from "src/spintr/SpintrApi";
import { FormControl, FormSection, FormTokenizedObjectInput } from "src/ui/components/Forms";
import SpintrLoader from "src/ui/components/Loader";
import ErrorMessagebar from "src/ui/components/Messagebars/ErrorMessagebar";
import SpintrDatePicker from "src/spintr/components/SpintrDatePicker";
import { SpintrTypes } from "src/typings";

interface IProps {
    courseId: number;
    onClose: () => void;
    onSuccess?: () => void;
    initialTargets?: any[];
}

interface IState {
    targets: any[];
    dueDate: Date | null;
    saveErrors: string[];
    isLoading: boolean;
}

const AcademyAssignCourseModal = (props: IProps) => {
    const [state, setState] = useState<IState>({
        targets: props.initialTargets || [],
        dueDate: null,
        saveErrors: [],
        isLoading: false,
    });

    const [isOpen, setIsOpen] = useState(true);
    const close = () => setIsOpen(false);

    const confirmPopupIsOpen = useSelector((s: any) => s?.popups?.confirmPopup?.isOpen);
    const blockDismissRef = useRef(false);

    useEffect(() => {
        if (confirmPopupIsOpen) {
            blockDismissRef.current = true;
            return;
        }

        if (blockDismissRef.current) {
            const timeoutId = window.setTimeout(() => {
                blockDismissRef.current = false;
            }, 300);

            return () => window.clearTimeout(timeoutId);
        }
    }, [confirmPopupIsOpen]);

    const onPanelDismiss = () => {
        if (blockDismissRef.current) {
            return;
        }

        close();
    };

    const onSaveClick = () => {
        setState((s) => ({ ...s, isLoading: true, saveErrors: [] }));

        api
            .post("/api/v1/academy/courses/" + props.courseId + "/assign", {
                targetIds: state.targets.map((t) => Number(t.key ?? t.id)),
                dueDate: state.dueDate ? state.dueDate : null,
            })
            .then(() => {
                props.onSuccess?.();
                close();
            })
            .catch((error: AxiosError<any>) => {
                let errors = [];

                if (error?.response?.data?.errorlist) {
                    errors = error.response.data.errorlist;
                } else {
                    errors.push(localize("TeknisktFel") + ".");
                }

                setState((s) => ({ ...s, saveErrors: errors, isLoading: false }));
            });
    };

    const onRenderFooterContent = () => (
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 12 }}>
            <DefaultButton
                text={localize("Avbryt")}
                onClick={close}
                disabled={state.isLoading}
            />
            <PrimaryButton
                text={localize("Spara")}
                onClick={onSaveClick}
                disabled={state.isLoading || state.targets.length === 0}
            />
        </Stack>
    );

    return (
        <Panel
            className="academy-assign-panel"
            isOpen={isOpen}
            onDismiss={onPanelDismiss}
            onDismissed={() => props.onClose()}
            headerText={localize("ASSIGN_COURSE")}
            type={PanelType.custom}
            customWidth="480px"
            isLightDismiss
            layerProps={{ eventBubblingEnabled: true }}
            focusTrapZoneProps={{ forceFocusInsideTrap: false, isClickableOutsideFocusTrap: true }}
            isFooterAtBottom
            onRenderFooterContent={onRenderFooterContent}
            allowTouchBodyScroll
        >
            {state.saveErrors.length > 0 && (
                <ErrorMessagebar
                    errorList={state.saveErrors}
                    onDismiss={() => setState((s) => ({ ...s, saveErrors: [] }))}
                />
            )}
            {state.isLoading && <SpintrLoader />}
            {!state.isLoading && (
                <FormSection>
                    <FormControl>
                        <FormTokenizedObjectInput
                            label={localize("ASSIGN_COURSE_TO")}
                            required
                            items={state.targets}
                            types={[
                                SpintrTypes.UberType.User,
                                SpintrTypes.UberType.Department,
                                SpintrTypes.UberType.Office,
                                SpintrTypes.UberType.Company,
                                SpintrTypes.UberType.Role,
                                SpintrTypes.UberType.TargetGroup,
                            ]}
                            onChange={(targets) => {
                                setState((s) => ({
                                    ...s,
                                    targets: targets.map((t) => ({ ...t, id: t.key || t.id })),
                                }));
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <SpintrDatePicker
                            label={localize("ASSIGN_DUE_DATE")}
                            minDate={new Date()}
                            value={state.dueDate}
                            isClearable
                            hideTimeSelect
                            onChangeHandler={(date: Date) => {
                                setState((s) => ({ ...s, dueDate: date }));
                            }}
                        />
                    </FormControl>
                </FormSection>
            )}
        </Panel>
    );
};

export default AcademyAssignCourseModal;
